import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../utils/api';

// Async thunks
export const fetchCollections = createAsyncThunk(
  'collections/fetchCollections',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/collections');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch collections');
    }
  }
);

export const fetchCollection = createAsyncThunk(
  'collections/fetchCollection',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/collections/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch collection');
    }
  }
);

export const createCollection = createAsyncThunk(
  'collections/createCollection',
  async (collectionData, { rejectWithValue }) => {
    try {
      const response = await api.post('/collections', collectionData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create collection');
    }
  }
);

export const updateCollection = createAsyncThunk(
  'collections/updateCollection',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/collections/${id}`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update collection');
    }
  }
);

export const deleteCollection = createAsyncThunk(
  'collections/deleteCollection',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/collections/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete collection');
    }
  }
);

export const runCollectionTests = createAsyncThunk(
  'collections/runCollectionTests',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.post(`/collections/${id}/run`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to run collection tests');
    }
  }
);

// Slice
const collectionsSlice = createSlice({
  name: 'collections',
  initialState: {
    collections: [],
    currentCollection: null,
    loading: false,
    error: null,
    runResults: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearRunResults: (state) => {
      state.runResults = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all collections
      .addCase(fetchCollections.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCollections.fulfilled, (state, action) => {
        state.loading = false;
        state.collections = action.payload;
      })
      .addCase(fetchCollections.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch single collection
      .addCase(fetchCollection.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCollection.fulfilled, (state, action) => {
        state.loading = false;
        state.currentCollection = action.payload;
      })
      .addCase(fetchCollection.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create collection
      .addCase(createCollection.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCollection.fulfilled, (state, action) => {
        state.loading = false;
        state.collections.push(action.payload);
      })
      .addCase(createCollection.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update collection
      .addCase(updateCollection.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCollection.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.collections.findIndex(c => c.id === action.payload.id);
        if (index !== -1) {
          state.collections[index] = action.payload;
        }
        if (state.currentCollection?.id === action.payload.id) {
          state.currentCollection = action.payload;
        }
      })
      .addCase(updateCollection.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete collection
      .addCase(deleteCollection.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCollection.fulfilled, (state, action) => {
        state.loading = false;
        state.collections = state.collections.filter(c => c.id !== action.payload);
        if (state.currentCollection?.id === action.payload) {
          state.currentCollection = null;
        }
      })
      .addCase(deleteCollection.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Run collection tests
      .addCase(runCollectionTests.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.runResults = null;
      })
      .addCase(runCollectionTests.fulfilled, (state, action) => {
        state.loading = false;
        state.runResults = action.payload;
      })
      .addCase(runCollectionTests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, clearRunResults } = collectionsSlice.actions;
export default collectionsSlice.reducer;

